import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import './PreviewGrid.css';

interface ScanPreview {
  scanId: string;
  timestamp: string;
  profile: string;
  summary: {
    pass: boolean;
    failCount: number;
    warnCount: number;
  };
  fileName: string;
  metadata?: {
    zipSize?: number;
  };
  screenshot?: string;
  runtime?: {
    performance?: {
      initialLoadBytes: number;
      totalLoadBytes: number;
    };
    viewport?: {
      width: number;
      height: number;
    };
  };
}

export const PreviewGrid: React.FC = () => {
  const [scans, setScans] = useState<ScanPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pass' | 'fail'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [dimensionFilter, setDimensionFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadScans();
  }, []);

  const loadScans = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/scans?limit=50'), { credentials: 'include' });
      const data = await response.json();

      const scansWithDetails = await Promise.all(
        (data.scans || []).map(async (scan: any) => {
          try {
            const detailResponse = await fetch(apiUrl(`/scans/${scan.scanId}`), { credentials: 'include' });
            const detailData = await detailResponse.json();
            return {
              scanId: scan.scanId,
              timestamp: scan.timestamp,
              profile: scan.profile || 'unknown',
              summary: detailData.result?.summary || { pass: false, failCount: 0, warnCount: 0 },
              fileName: detailData.result?.metadata?.originalFileName || 'Unknown',
              screenshot: detailData.result?.runtime?.screenshot?.base64,
              runtime: detailData.result?.runtime,
              metadata: { zipSize: detailData.result?.metadata?.zipSize }
            };
          } catch (error) {
            return {
              scanId: scan.scanId,
              timestamp: scan.timestamp,
              profile: scan.profile || 'unknown',
              summary: { pass: false, failCount: 0, warnCount: 0 },
              fileName: scan.scanId.substring(0, 8),
              screenshot: undefined,
              runtime: undefined
            };
          }
        })
      );
      
      setScans(scansWithDetails);
    } catch (error) {
      console.error('Failed to load scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteScan = async (scanId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this scan?')) return;

    setDeletingId(scanId);
    try {
      const response = await fetch(apiUrl(`/scans/${scanId}`), { method: 'DELETE', credentials: 'include' });
      if (response.ok) {
        setScans(prev => prev.filter(s => s.scanId !== scanId));
      }
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const uniqueDimensions = Array.from(
    new Set(
      scans
        .filter(s => s.runtime?.viewport)
        .map(s => `${s.runtime!.viewport!.width}x${s.runtime!.viewport!.height}`)
    )
  ).sort();

  const filteredScans = scans.filter(scan => {
    if (filter === 'pass' && !scan.summary.pass) return false;
    if (filter === 'fail' && scan.summary.pass) return false;
    
    if (dimensionFilter !== 'all' && scan.runtime?.viewport) {
      const scanDimension = `${scan.runtime.viewport.width}x${scan.runtime.viewport.height}`;
      if (scanDimension !== dimensionFilter) return false;
    }
    
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchesName = scan.fileName.toLowerCase().includes(search);
      const matchesId = scan.scanId.toLowerCase().includes(search);
      if (!matchesName && !matchesId) return false;
    }
    
    return true;
  });

  const sortedScans = [...filteredScans].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    } else if (sortBy === 'name') {
      comparison = a.fileName.localeCompare(b.fileName);
    } else if (sortBy === 'size') {
      const aSize = a.runtime?.performance?.totalLoadBytes || a.metadata?.zipSize || 0;
      const bSize = b.runtime?.performance?.totalLoadBytes || b.metadata?.zipSize || 0;
      comparison = aSize - bSize;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const formatBytes = (bytes: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleScanClick = (scanId: string) => {
    window.location.href = `/scans/${scanId}`;
  };

  return (
    <div className="gallery-container">
      {/* Header */}
      <div className="gallery-header">
        <h1>Creative Gallery</h1>
        <div className="header-controls">
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
          />
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)} className="filter-select">
            <option value="all">All ({scans.length})</option>
            <option value="pass">Passed</option>
            <option value="fail">Failed</option>
          </select>
          {uniqueDimensions.length > 0 && (
            <select value={dimensionFilter} onChange={(e) => setDimensionFilter(e.target.value)} className="filter-select">
              <option value="all">All Sizes</option>
              {uniqueDimensions.map(dim => (
                <option key={dim} value={dim}>{dim}</option>
              ))}
            </select>
          )}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="filter-select">
            <option value="date">Date</option>
            <option value="name">Name</option>
            <option value="size">Size</option>
          </select>
          <button onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')} className="sort-btn">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
          <button onClick={loadScans} className="refresh-btn">↻</button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      ) : sortedScans.length === 0 ? (
        <div className="empty-state">
          <p>No scans found</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {sortedScans.map(scan => (
            <div
              key={scan.scanId}
              className={`gallery-card ${scan.summary.pass ? 'pass' : 'fail'}`}
              onClick={() => handleScanClick(scan.scanId)}
            >
              {/* Screenshot */}
              <div className="card-preview">
                {scan.screenshot ? (
                  <img src={`data:image/jpeg;base64,${scan.screenshot}`} alt={scan.fileName} />
                ) : (
                  <div className="no-preview">No Preview</div>
                )}
                {/* Status badge overlay */}
                <div className={`status-badge ${scan.summary.pass ? 'pass' : 'fail'}`}>
                  {scan.summary.pass ? '✓' : '✗'}
                </div>
              </div>

              {/* Card info */}
              <div className="card-info">
                <h3>{scan.fileName}</h3>
                <div className="card-meta">
                  <span>{formatDate(scan.timestamp)}</span>
                  {scan.runtime?.viewport && (
                    <span>{scan.runtime.viewport.width}×{scan.runtime.viewport.height}</span>
                  )}
                  {(scan.runtime?.performance?.totalLoadBytes || scan.metadata?.zipSize) && (
                    <span>{formatBytes(scan.runtime?.performance?.totalLoadBytes || scan.metadata?.zipSize || 0)}</span>
                  )}
                </div>
                {!scan.summary.pass && (
                  <div className="card-issues">
                    <span className="issue-count">{scan.summary.failCount} issue{scan.summary.failCount !== 1 ? 's' : ''}</span>
                    {scan.summary.warnCount > 0 && (
                      <span className="warn-count">{scan.summary.warnCount} warning{scan.summary.warnCount !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Delete button */}
              <button 
                className="delete-btn"
                onClick={(e) => deleteScan(scan.scanId, e)}
                disabled={deletingId === scan.scanId}
              >
                {deletingId === scan.scanId ? '...' : '×'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
